import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, FileDown, Loader2, Library } from "lucide-react";

export default function Purchases() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/purchases").then((r) => setNotes(r.data)).finally(() => setLoading(false));
  }, []);

  const download = async (note) => {
    setDownloading(note.id);
    try {
      const res = await api.get(`/notes/${note.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = note.original_filename || `${note.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
      <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900 mb-2">My Notes</h1>
      <p className="text-muted-foreground mb-8">Download the notes you've purchased anytime.</p>

      {notes.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl" data-testid="purchases-empty">
          <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Library className="h-7 w-7 text-slate-400" />
          </div>
          <p className="text-lg font-medium text-slate-900">No purchases yet</p>
          <p className="text-muted-foreground mt-1">Notes you buy will appear here.</p>
          <Link to="/">
            <Button className="mt-6 rounded-full">Browse notes</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="purchases-list">
          {notes.map((n) => (
            <div key={n.id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-white" data-testid={`purchase-${n.id}`}>
              <div className="h-14 w-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                {n.has_cover ? (
                  <img src={`${API}/notes/${n.id}/cover`} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-white/80" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/note/${n.id}`} className="font-medium text-slate-900 hover:text-primary line-clamp-1">
                  {n.title}
                </Link>
                <p className="text-xs text-muted-foreground">{n.subject} · by {n.seller_name}</p>
              </div>
              <Button
                className="rounded-full gap-2"
                onClick={() => download(n)}
                disabled={downloading === n.id}
                data-testid={`download-${n.id}`}
              >
                {downloading === n.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                Download
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
